import { ColDef } from "ag-grid-community";

import { ColumnType } from "components/AgGrid/constants";

import { erpStatusRenderer } from "lib/agGrid/columns/erpStatus";
import { euStatusRenderer } from "lib/agGrid/columns/euStatus";
import { trueIcon } from "lib/agGrid/columns/booleanRenderers";
import { lifetimeTraceableRenderer } from "lib/agGrid/columns/lifetimeTraceable";
import {
  kilogramsFormatter,
  kilogramsGetter,
} from "lib/agGrid/columns/totalMassGrams";
import {
  weightOutlierComparator,
  weightOutlierGetter,
  weightOutlierRenderer,
} from "lib/agGrid/columns/weightOutlier";
import getters from "lib/agGrid/getters";
import {
  nullableBooleanRenderer,
  ScanLabelsRenderer,
} from "lib/agGrid/renderers";
import nlisIdRenderer from "lib/agGrid/renderers/nlisIdRenderer";

export const ScanColId = {
  ARRIVAL_PIC: "arrival_pic",
  CREATED: "created",
  CURRENT_PIC: "current_pic",
  EID: "EID",
  ERP_STATUS: "ERP_status",
  EU_STATUS: "EU_status",
  IS_ANGUS_VERIFIED: "isAngusVerified",
  IS_MANUALLY_WEIGHED: "a883f41d-21b1-42e0-afd6-e9e482a71b31",
  IS_NLIS_SIGHTED: "is_nlis_sighted",
  LABELS: "scan_labels",
  LIFETIME_TRACEABILITY: "lifetime_traceability",
  NLIS_ID: "nlis_id",
  SCAN_TIME_WEIGHED: "time_weighed",
  TOTAL_MASS_GRAMS: "totalMassGrams",
  TOTAL_MASS_GRAMS_OUTLIER: "totalMassGramsOutlier",
} as const;

interface ScanAgGridRowType {
  scan: Scan;
  scanLabels?: ScanLabel[];
}

export type ScanColumnDefinitionsAgGridRowType = Record<
  ValueInObj<typeof ScanColId>,
  ColDef<ScanAgGridRowType>
>;

export const ScanColumnDefinitions: ScanColumnDefinitionsAgGridRowType = {
  [ScanColId.TOTAL_MASS_GRAMS]: {
    colId: ScanColId.TOTAL_MASS_GRAMS,
    field: "scan.total_mass_grams",
    headerName: "Weight (Kg)",
    width: 95,
    type: ColumnType.NUMERIC,
    valueGetter: kilogramsGetter,
    valueFormatter: kilogramsFormatter,
  },
  [ScanColId.TOTAL_MASS_GRAMS_OUTLIER]: {
    colId: ScanColId.TOTAL_MASS_GRAMS_OUTLIER,
    field: "scan.total_mass_grams",
    headerName: "In Range?",
    width: 80,
    valueGetter: weightOutlierGetter,
    cellRenderer: weightOutlierRenderer,
    comparator: weightOutlierComparator,
  },
  [ScanColId.IS_MANUALLY_WEIGHED]: {
    colId: ScanColId.IS_MANUALLY_WEIGHED,
    field: "scan.is_manually_weighed",
    headerName: "Manually Weighed",
    width: 135,
    cellRenderer: trueIcon,
  },
  [ScanColId.EID]: {
    colId: ScanColId.EID,
    field: "scan.EID",
    headerName: "EID",
    width: 120,
  },
  [ScanColId.NLIS_ID]: {
    colId: ScanColId.NLIS_ID,
    field: "scan.animal.nlis_id",
    headerName: "NLIS Id",
    width: 120,
    cellRenderer: nlisIdRenderer,
    valueGetter: getters.nlisIdGetter,
  },
  [ScanColId.LABELS]: {
    colId: ScanColId.LABELS,
    headerName: "Scan Labels",
    field: "scanLabels",
    width: 150,
    valueGetter: ({ data }) =>
      data?.scanLabels?.map(label => label.name || label.archetype) || null,
    filterValueGetter: ({ data }) =>
      data?.scanLabels?.map(label => label.name || label.archetype) || null,
    cellRenderer: ScanLabelsRenderer,
    type: ColumnType.STRING,
  },
  [ScanColId.ERP_STATUS]: {
    colId: ScanColId.ERP_STATUS,
    field: "scan.ERP_status",
    headerName: "ERP",
    width: 65,
    valueGetter: getters.erpGetter,
    cellRenderer: erpStatusRenderer,
    enablePivot: true,
    enableRowGroup: true,
  },
  [ScanColId.EU_STATUS]: {
    colId: ScanColId.EU_STATUS,
    field: "scan.EU_status",
    headerName: "EU",
    width: 50,
    cellRenderer: euStatusRenderer,
    valueGetter: getters.euGetter,
    enablePivot: true,
    enableRowGroup: true,
  },
  [ScanColId.IS_ANGUS_VERIFIED]: {
    colId: ScanColId.IS_ANGUS_VERIFIED,
    field: "scan.animal.is_angus_verified",
    headerName: "Angus Verified",
    filterParams: {
      showIndeterminate: true,
    },
    width: 100,
    cellRenderer: nullableBooleanRenderer,
  },
  [ScanColId.CURRENT_PIC]: {
    colId: ScanColId.CURRENT_PIC,
    field: "scan.current_pic",
    headerName: "Current PIC",
    width: 100,
    enablePivot: true,
    enableRowGroup: true,
  },
  [ScanColId.ARRIVAL_PIC]: {
    colId: ScanColId.ARRIVAL_PIC,
    field: "scan.arrival_pic",
    headerName: "Arrival PIC",
    width: 100,
    enablePivot: true,
    enableRowGroup: true,
  },
  [ScanColId.LIFETIME_TRACEABILITY]: {
    colId: ScanColId.LIFETIME_TRACEABILITY,
    field: "scan.lifetime_traceability",
    headerName: "LT",
    width: 50,
    cellRenderer: lifetimeTraceableRenderer,
    valueGetter: getters.erpGetter,
    enablePivot: true,
    enableRowGroup: true,
  },
  [ScanColId.IS_NLIS_SIGHTED]: {
    colId: ScanColId.IS_NLIS_SIGHTED,
    field: "scan.is_nlis_sighted",
    headerName: "Sighted",
    width: 135,
    cellRenderer: trueIcon,
  },
  [ScanColId.CREATED]: {
    colId: ScanColId.CREATED,
    field: "scan.created",
    headerName: "Created",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
    width: 130,
  },
  [ScanColId.SCAN_TIME_WEIGHED]: {
    colId: ScanColId.SCAN_TIME_WEIGHED,
    field: "scan.time_weighed",
    headerName: "Weigh Time",
    width: 105,
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
};
